import {numberToVoltageLevel} from '../model/voltage_levels';
import {MapAsset} from './map_asset';
import {DEFAULT_POLYLINE_STROKE_COLOR_BLACK, LINE_COLOR_BY_VOLTAGE_LEVEL} from './shape_factory';

/**
 * Type which represents custom polyline type which can be consumed by `AssetMap` component and
 * displayed on the map.
 * Objects which implement this interface should hold logic of how they are being changed by user
 * iterations.
 */
export interface AssetMapLine {
  setMap(map: google.maps.Map | null): void;
  highlight(enable: boolean, unselect: boolean): void;
  addListener(event: string, callback: () => void): void;
  getBounds(): google.maps.LatLngBounds;
  setOptions(options: google.maps.PolylineOptions): void;
}

const HOVER_EVENT = 'mouseover';
const UNHOVER_EVENT = 'mouseout';

/**
 * Factory method for generating polylines for assets with multiple location points.
 */
export function assetMapLinesFactory(
  asset: MapAsset,
  highlight: boolean,
  options: google.maps.PolylineOptions | null = null,
): AssetMapLine | null {
  if (asset.location.length < 2) {
    return null;
  }

  return new DefaultMapPolyline(asset, highlight, options);
}

/**
 * Default representation of the polyline where color of the line is based on the base voltage.
 */
export class DefaultMapPolyline implements AssetMapLine {
  protected readonly enabledOptions: google.maps.PolylineOptions;
  protected readonly unselectedOptions: google.maps.PolylineOptions;
  protected readonly selectedOptions: google.maps.PolylineOptions | null;
  protected readonly hoveredOptions: google.maps.PolylineOptions | null;
  protected readonly hoveredUnselectedOptions: google.maps.PolylineOptions | null;

  private readonly polyline: google.maps.Polyline;

  protected isEnable: boolean;
  protected isSelected = false;

  constructor(
    protected readonly asset: MapAsset,
    highlight = true,
    protected readonly defaultOptions: google.maps.PolylineOptions | null = null,
  ) {
    this.isEnable = highlight;

    this.enabledOptions = this.getEnabledOptions();
    this.unselectedOptions = this.getUnselectedOptions();
    this.selectedOptions = this.getSelectedOptions();
    this.hoveredOptions = this.getHoveredOptions();
    this.hoveredUnselectedOptions = this.getHoveredUnselectedOptions();

    this.polyline = new google.maps.Polyline({
      path: asset.location,
      strokeColor: this.getLineColor(asset),
      ...(highlight ? this.enabledOptions : this.unselectedOptions),
    });

    if (this.hoveredOptions) {
      this.polyline.addListener(HOVER_EVENT, () => {
        this.polyline.setOptions(
          this.polyline
            ? this.hoveredOptions!
            : this.hoveredUnselectedOptions || this.unselectedOptions,
        );
      });
      this.polyline.addListener(UNHOVER_EVENT, () => {
        if (this.isSelected) {
          this.polyline.setOptions(this.selectedOptions);
        } else {
          this.polyline.setOptions(this.isEnable ? this.enabledOptions : this.unselectedOptions);
        }
      });
    }
  }

  setMap(map: google.maps.Map | null) {
    this.polyline.setMap(map);
  }

  setOptions(options: google.maps.PolylineOptions) {
    this.polyline.setOptions(options);
  }

  /**
   * Controls the style of the line when highlight of the marker is necessary.
   */
  highlight(enable: boolean, unselect: boolean) {
    this.isSelected = !unselect && enable;
    this.isEnable = enable;

    if (this.isSelected) {
      // Ensure selected options are applied in the latest browser re-paint,
      // otherwise it works inconsistently.
      requestAnimationFrame(() => {
        this.polyline.setOptions(this.selectedOptions);
      });
    } else {
      this.polyline.setOptions(this.isEnable ? this.enabledOptions : this.unselectedOptions);
    }
  }

  addListener(eventName: string, callback: (event: google.maps.MapMouseEvent) => void) {
    this.polyline.addListener(eventName, callback);
  }

  getBounds(): google.maps.LatLngBounds {
    const bounds = new google.maps.LatLngBounds();

    for (const point of this.asset.location) {
      bounds.extend(point);
    }

    return bounds;
  }

  getLineColor(asset: MapAsset): string {
    if (asset.properties.baseVoltage) {
      return LINE_COLOR_BY_VOLTAGE_LEVEL.get(numberToVoltageLevel(asset.properties.baseVoltage));
    }

    return DEFAULT_POLYLINE_STROKE_COLOR_BLACK;
  }

  protected getEnabledOptions(): google.maps.PolylineOptions {
    return {
      ...this.defaultOptions,
      zIndex: 1,
      strokeOpacity: 1,
      strokeWeight: 4,
    };
  }

  protected getUnselectedOptions(): google.maps.PolylineOptions {
    return {
      ...this.defaultOptions,
      zIndex: 1,
      strokeOpacity: 0.5,
      strokeWeight: 4,
    };
  }

  protected getSelectedOptions(): google.maps.PolylineOptions | null {
    return {
      ...this.defaultOptions,
      zIndex: 2,
      strokeOpacity: 1,
      strokeWeight: 10,
    };
  }

  protected getHoveredOptions(): google.maps.PolylineOptions | null {
    return {
      ...this.defaultOptions,
      zIndex: 3,
      strokeOpacity: 1,
      strokeWeight: 8,
    };
  }

  protected getHoveredUnselectedOptions(): google.maps.PolylineOptions | null {
    return {
      ...this.defaultOptions,
      zIndex: 3,
      strokeOpacity: 0.7,
      strokeWeight: 8,
    };
  }
}
